// TODO: Divide types into DTOs, models, and payloads
export interface TaskAssignee {
  first_name: string;
  img: string;
  last_name: string;
  nickname: string;
  status: string;
  user_id: string;
}

export interface TaskChecklistItem {
  completed_at: string | null;
  due_at: string | null;
  id: string;
  is_completed: boolean;
  name: string;
  order: number;
  user_id: string | null;
}

export type TaskChecklistSummaryItem = {
  id: string;
  due_at: string | null;
  name: string;
  completed_at: string;
  task_id: string;
  task_name: string;
};

export type ChecklistSummary = {
  checklists: TaskChecklistSummaryItem[];
  user_id: string;
};

export type TaskLabel = {
  id: string;
  text: string;
  color: string;
  order: number;
};

export enum TaskPriority {
  none = "none",
  low = "low",
  medium = "medium",
  high = "high",
}

export enum TaskDefaultStatus {
  notStarted = "not-started",
  inProgress = "in-progress",
  completed = "completed",
  reopened = "reopened",
}

export interface TaskCustomStatus {
  id: string;
  order: number;
  text: string;
}

export enum TaskType {
  voice = "voice_task",
  overdue = "overdue",
  recurring = "recurring",
  template = "template",
}

export enum TaskSortBy {
  DueAt = "due_at",
}

export type TaskAttachment = {
  content_type: string;
  id: string;
  name: string;
  preview_available: boolean;
  preview_url: string;
  size: number;
  status: "valid" | "invalid";
  stream_url: string;
  updated_at: string;
};

export type TaskCustomer = {
  id: string;
  logo: string | null;
  fields: { id: string; name: string; value: string }[];
};

export type TaskCreator = {
  first_name: string;
  img: string | null;
  last_name: string;
  nickname: string;
  // TODO: import status from user
  status: unknown;
  user_id: string;
};

export interface BaseTask {
  assignees: string[];
  can_assignee_edit: boolean;
  chklist_overdue: boolean;
  created_by: string;
  created_at: string;
  cust_id: string | null;
  custom_fields: Record<string, string> | null;
  due_at: string | null;
  duration: number;
  id: string;
  entity_id: string;
  is_self: boolean;
  label_id: string | null;
  name: string;
  priority: TaskPriority;
  short_url: string;
  status: TaskDefaultStatus | string;
  unread: boolean;
  updated_at: string;
}

export interface PreviewTask extends BaseTask {
  chklist_completed: number;
  chklist_total: number;
}

export interface DetailedTask extends BaseTask {
  attachments: TaskAttachment[];
  checklist: TaskChecklistItem[];
  completed_on: string | null;
  desc: string | null;
  has_chklist_chk: boolean;
  is_recurring: boolean;
  type: string | null;
  _is_deleted?: boolean;
}

export type TaskCustomFieldType = "text" | "number" | "date" | "dropdown";

export type TaskCustomField = {
  field: string;
  field_type: TaskCustomFieldType;
  id: string;
  order: number;
  show_in_card?: boolean;
  placeholder?: string;
  values?: null | Record<string, unknown>[];
};

export interface TaskFilters {
  assignee: (string | null)[] | null;
  custom_fields: Record<string, (string | null)[]> | null;
  customer: (string | null)[] | null;
  label: (string | null)[] | null;
  overdue: true | null;
  priority: TaskPriority[] | null;
  recurring: true | null;
  status: (TaskDefaultStatus | string)[] | null;
}

export interface TaskFilterSet extends TaskFilters {
  id: string;
  name: string;
  updated_at: string;
}

export interface TasksInitData {
  custom_fields: {
    entity_id: string;
    id: string;
    props: {
      value: TaskCustomField[];
    };
    type: "task_fields";
  };
  custom_statuses: TaskCustomStatus[];
  filter_sets: TaskFilterSet[];
  labels: TaskLabel[];
}

export type ExpectedTaskCustomDropdownFieldValue = {
  id: string;
  value: string;
  order: number;
};

export type InternalTaskCustomField = {
  id: string;
  name: string;
  type: TaskCustomFieldType;
  order: number;
  isShownInCard: boolean;
  values?: ExpectedTaskCustomDropdownFieldValue[];
};

export interface InternalTaskFilters {
  assignees: (string | null)[];
  customers: (string | null)[];
  customFields: Record<string, (string | null)[]>;
  isOverdue: true | null;
  isRecurring: true | null;
  labels: (string | null)[];
  priorities: TaskPriority[];
  statuses: (TaskDefaultStatus | string)[];
}

export interface InternalTaskFilterSet {
  id: string;
  name: string;
  filters: InternalTaskFilters;
  updatedAt: string;
}

export interface TaskFields {
  customFieldsData: {
    id: string;
    items: InternalTaskCustomField[];
  };
  labels: TaskLabel[];
  customStatuses: TaskCustomStatus[];
  filterSets: InternalTaskFilterSet[];
}

type CreatedActionChanges = { title: string; time: string };

export type UpdatedActionChanges = Partial<
  Pick<
    DetailedTask,
    | "name"
    | "desc"
    | "priority"
    | "due_at"
    | "status"
    | "duration"
    | "can_assignee_edit"
    | "has_chklist_chk"
  >
> & {
  assignee_added?: {
    first_name: string;
    last_name: string;
    user_id: string;
  }[];
  assignee_removed?: {
    first_name: string;
    last_name: string;
    user_id: string;
  }[];
  checklist_updated?: {
    id: string;
    c_name: string;
    name?: string;
    is_completed?: boolean;
    completed_at?: string;
  }[];
  checklist_rearranged?: [];
  checklist_deleted?: {
    id: string;
    c_name: string;
  }[];
  checklist_added?: {
    id: string;
    c_name: string;
  }[];
  custom_fields?: DetailedTask["custom_fields"];
  label?: Omit<TaskLabel, "order">;
  old_custom_fields?: DetailedTask["custom_fields"];
  attachment_added?: {
    file_name: string;
  };
  attachment_deleted?: {
    file_name: string;
  };
  attachment_renamed?: {
    file_name: string;
    old_file_name: string;
  };
  customer?: "updated";
};

type DeletedActionChanges = CreatedActionChanges;

type TaskActivityTaskInfo = {
  id: string;
  title: string;
};
export interface TaskActivity {
  action_by: {
    user_id: string;
    first_name: string;
    last_name: string;
    img: string | null;
    updated_at: string;
    logo?: string;
  };
  action_details:
    | {
        action: "created";
        changes: CreatedActionChanges;
        task: TaskActivityTaskInfo;
      }
    | {
        action: "updated";
        changes: UpdatedActionChanges;
        task: TaskActivityTaskInfo;
      }
    | {
        action: "deleted";
        changes: DeletedActionChanges;
        task: TaskActivityTaskInfo;
      }
    | { action: "viewed" }
    | {
        action: "commented";
        changes: { content: string };
        task: TaskActivityTaskInfo;
      }
    | {
        action: "downloaded";
        file_name: string;
      }
    | {
        action: "guest_comment";
        changes: { content: string };
        task: TaskActivityTaskInfo;
      }
    | {
        action: "guest_opened";
        changes: { content: string };
        task: TaskActivityTaskInfo;
      }
    | {
        action: "guest_comment_reply";
        changes: { content: string };
        task: TaskActivityTaskInfo;
      }
    | {
        action: "guest_feedback";
        changes: {
          content: {
            feedback: string;
            hashtag: string[];
            rating: number;
          };
        };
        task: TaskActivityTaskInfo;
      };
  id: string;
  inserted_at: string;
  task_id: string;
  updated_at: string;
}

export interface SelfProducedTaskActivity {
  action_by: string;
  action_details: TaskActivity["action_details"];
  inserted_at: string;
}

export interface UpdateTaskResponse {
  task: DetailedTask;
  activity: SelfProducedTaskActivity | Record<string, never>;
}

export type CreateTaskPayload = Pick<
  DetailedTask,
  | "assignees"
  | "can_assignee_edit"
  | "cust_id"
  | "custom_fields"
  | "desc"
  | "due_at"
  | "has_chklist_chk"
  | "label_id"
  | "name"
  | "priority"
  | "status"
  | "checklist"
> & {
  is_self?: boolean;
  as_individual_tasks?: boolean;
};

export type CreateTaskCustomFieldPayload = Pick<
  TaskCustomField,
  "field" | "field_type" | "show_in_card" | "order"
> & { values?: ExpectedTaskCustomDropdownFieldValue[] };

export type UpdateTaskCustomFieldPayload = CreateTaskCustomFieldPayload &
  Pick<TaskCustomField, "id">;
